/**
 * Setup Wizard API service
 * For users in pending_setup status to complete their account setup
 */

import { API_BASE_URL, isCapacitor } from './config';

interface ApiResponse<T> {
  data: T;
}

export interface SetupModule {
  id: string;
  key: string;
  name_key: string;
  description_key: string;
  icon: string;
  /**
   * Sharing Infra P-4: when set, this module exposes a tenant-wide default
   * visibility mode (the object_type it keys, e.g. "pipeline.deal"). The wizard
   * shows a per-module visibility question and seeds `entity_visibility_modes`.
   */
  visibility_object_type?: string | null;
}

/** A demo-data variation offered in the onboarding picker. */
export interface SetupDemoVariation {
  key: string;
  name: string;
  description: string;
  available: boolean;
}

/** The onboarding variation catalog + the industry-recommended key (§4.4). */
export interface SetupDemoCatalog {
  variations: SetupDemoVariation[];
  recommended_variation: string;
}

export interface InvitationOption {
  type: 'invitation';
  tenant_id: string;
  tenant_name: string;
  /** Null when the inviter had no primary company; the join resolves the tenant default. */
  company_id: string | null;
  company_name: string | null;
  invited_by_name: string | null;
  invited_by_email: string | null;
  is_admin: boolean;
}

export interface DomainMatchOption {
  type: 'domain_match';
  tenant_id: string;
  tenant_name: string;
  companies: Array<{ id: string; name: string }>;
}

export type SetupOption = InvitationOption | DomainMatchOption;

/**
 * Domain matched an active tenant whose self-registration is OFF: the user can
 * request to join (tenant-admin approval) rather than being forced to create a
 * duplicate new tenant. Shape mirrors DomainMatchOption.
 */
export interface DomainMatchRequestOption {
  tenant_id: string;
  tenant_name: string;
  companies: Array<{ id: string; name: string }>;
}

export interface SetupOptionsResponse {
  has_invitation: boolean;
  has_domain_match: boolean;
  invitation: InvitationOption | null;
  domain_match: DomainMatchOption | null;
  has_domain_match_request: boolean;
  domain_match_request: DomainMatchRequestOption | null;
}

/** A pending request to join a tenant (returned by GET /setup-wizard/join-request). */
export interface MyJoinRequest {
  id: string;
  tenant_id: string;
  tenant_name: string | null;
  company_id: string | null;
  status: string;
  created_at: string | null;
}

export interface JoinTenantRequest {
  tenant_id: string;
  /** Null for an invitation that names no company — the server resolves the tenant default. */
  company_id: string | null;
  is_admin?: boolean;
  enabled_modules?: string[] | null;
  job_function?: string | null;
  job_function_custom?: string | null;
  job_level?: string | null;
  timezone?: string | null;
}

export interface CreateTenantRequest {
  company_name: string;
  enabled_modules?: string[] | null;
  /**
   * Sharing Infra P-4: per-object_type default visibility mode chosen in the
   * wizard (e.g. { "pipeline.deal": "owner_and_shared" }). `everyone` and
   * non-configurable types are skipped server-side (absent row == everyone).
   */
  visibility_modes?: Record<string, string> | null;
  customer_types?: string[] | null;
  operating_countries?: string[] | null;
  is_global_operation?: boolean;
  sells_physical_products?: boolean;
  sells_services_by_project?: boolean;
  sells_services_by_time?: boolean;
  sells_subscriptions?: boolean;
  sells_logistics_services?: boolean;
  job_function?: string | null;
  job_function_custom?: string | null;
  job_level?: string | null;
  claim_subdomain?: string | null;
  allow_domain_self_registration?: boolean;
  timezone?: string | null;
  // --- v2 wizard fields (Onboarding #5/#6 backend) ---
  /** ISO 3166-1 alpha-2 country code for the default company. */
  company_country?: string | null;
  /** Single industry vertical; the backend derives the vertical list from it. */
  industry?: string | null;
  /** Hex brand color, e.g. '#3b82f6'. */
  brand_color?: string | null;
  /** File ID of the tenant logo (file_management). */
  logo_file_id?: string | null;
  /** Public website URL of the tenant. */
  company_website?: string | null;
  /** Long-form description shown on the company profile page. */
  company_description?: string | null;
  /** Headcount band (e.g. 'small', 'medium'). */
  company_size_band?: string | null;
  /** User's public LinkedIn URL (from /confirm-person). */
  linkedin_url?: string | null;
  /** User's one-line tagline (from /confirm-person). */
  headline?: string | null;
  /** File ID for the user's profile photo. */
  avatar_file_id?: string | null;
  /** Photo URL to download server-side after tenant creation (from /confirm-person or Gravatar). */
  avatar_photo_url?: string | null;
  /**
   * Base64 image data URL for a photo the user picked in the wizard, stored
   * server-side after tenant creation. Takes precedence over `avatar_photo_url`.
   */
  avatar_photo_data_url?: string | null;
}

// --- v2 prefill / person contracts (Onboarding #5 + #6) ---

export type PrefillConfidence = 'high' | 'medium' | 'low';
export type PrefillStatus = 'pending' | 'ready' | 'failed';

export interface PrefillField<T> {
  value: T | null;
  confidence: PrefillConfidence;
}

export interface CompanyPrefill {
  name: PrefillField<string>;
  website: PrefillField<string>;
  industry: PrefillField<string>;
  customer_types: PrefillField<string[]>;
  size_band: PrefillField<string>;
  primary_country: PrefillField<string>;
  description: PrefillField<string>;
  logo_file_id: string | null;
  brand_color: string | null;
}

export interface PrefillPersonCandidate {
  linkedin_url: string;
  headline_snippet: string;
  photo_thumb_url: string | null;
  raw_score: number;
  normalized_confidence: PrefillConfidence;
}

export interface PersonPrefill {
  candidates: PrefillPersonCandidate[];
  best_candidate_id: string | null;
}

export interface PrefillDefaults {
  country_code: string | null;
  timezone: string | null;
  language: string | null;
}

export interface SetupWizardPrefill {
  status: PrefillStatus;
  /**
   * True when the user registered with a free/consumer email domain
   * (gmail/yahoo/…). The domain enrichment lane is skipped for these, so the
   * wizard opens Step 1 in "ask + fetch + confirm" lookup mode (Onboarding #8)
   * instead of a blank Confirm-Company form.
   */
  generic_domain: boolean;
  company: CompanyPrefill;
  person: PersonPrefill;
  defaults: PrefillDefaults;
}

export interface ConfirmPersonRequest {
  /** LinkedIn URL the user confirmed (or pasted manually). */
  linkedin_url: string;
  /** Original search snippet; used as AI extraction input if the LinkedIn page can't be fetched. */
  snippet_hint?: string | null;
}

export interface PersonProfile {
  linkedin_url: string;
  full_name: string | null;
  headline: string | null;
  role: string | null;
  photo_url: string | null;
  photo_file_id: string | null;
  snippet_only: boolean;
  errors: string[];
}

export interface FallbackResolveResponse {
  domain: string;
  company: CompanyPrefill;
  defaults: PrefillDefaults;
}

export interface SubdomainSuggestion {
  can_claim: boolean;
  suggested_subdomain: string | null;
  full_domain: string | null;
  base_domain: string | null;
  email_domain: string | null;
}

/** Machine-readable outcome of a live subdomain availability check. */
export type SubdomainAvailabilityReason =
  | 'available'
  | 'invalid_format'
  | 'too_short'
  | 'too_long'
  | 'reserved'
  | 'taken';

export interface SubdomainAvailability {
  available: boolean;
  reason: SubdomainAvailabilityReason;
  full_domain: string | null;
  base_domain: string;
}

export interface SetupCompleteResponse {
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    status: string;
    is_admin: boolean;
    tenant_id: string;
    company_id: string;
  };
  tenant?: {
    id: string;
    name: string;
  };
  company?: {
    id: string;
    name: string;
  };
  custom_domain?: {
    id: string;
    domain: string;
    status: string;
  };
  /**
   * Auth tokens are issued only when the tenant goes live immediately. When the
   * sysadmin tenant-approval gate is on the tenant + user are held in
   * `pending_approval` and NO tokens are returned — `pending_approval` is true
   * instead (the wizard then shows the "application received" screen).
   */
  access_token?: string;
  refresh_token?: string;
  /** True when the gate held the tenant for sysadmin review (no tokens issued). */
  pending_approval?: boolean;
  /** Message code for the pending-approval result (`TENANT_PENDING_APPROVAL`). */
  message_code?: string;
}

class SetupWizardApiError extends Error {
  code: string;
  details?: Record<string, string>;

  constructor(code: string, message: string, details?: Record<string, string>) {
    super(message);
    this.code = code;
    this.details = details;
    this.name = 'SetupWizardApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json();

  if (!response.ok) {
    const errorInfo = data.detail || data.error;
    throw new SetupWizardApiError(
      errorInfo?.code || 'UNKNOWN_ERROR',
      errorInfo?.message || 'An error occurred',
      errorInfo?.details
    );
  }

  return (data as ApiResponse<T>).data;
}

/**
 * Like {@link handleResponse} but for endpoints that return the model directly
 * (no ``{ data: ... }`` envelope) — e.g. the demo-data routes.
 */
async function handleRawResponse<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok) {
    const errorInfo = data.detail || data.error;
    throw new SetupWizardApiError(
      errorInfo?.code || 'UNKNOWN_ERROR',
      errorInfo?.message || 'An error occurred',
      errorInfo?.details
    );
  }
  return data as T;
}

function getAuthHeaders(token: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'X-PanLuma-Client': isCapacitor ? 'mobile' : 'web',
  };
}

export const setupWizardApi = {
  /**
   * Get available setup options for the current pending user
   */
  async getOptions(token: string): Promise<SetupOptionsResponse> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/options`, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    return handleResponse<SetupOptionsResponse>(response);
  },

  /**
   * Get detected country code based on client IP (GeoIP)
   */
  async getGeoipCountry(token: string): Promise<{ country_code: string | null }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/geoip-country`, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    return handleResponse<{ country_code: string | null }>(response);
  },

  /**
   * Whether a self-serve-created tenant will be held for sysadmin approval.
   * Drives the create-tenant step's application-vs-instant-create copy.
   */
  async getTenantApprovalRequired(token: string): Promise<{ require_tenant_approval: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/tenant-approval-required`, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    return handleResponse<{ require_tenant_approval: boolean }>(response);
  },

  /**
   * Unauthenticated geo-IP locale defaults for pre-auth screens (register/login).
   * Returns the detected country plus the UI language (restricted to the app's
   * supported set) and ISO currency derived from it — so the registration page
   * can default to Hebrew/ILS in Israel before the visitor has logged in.
   */
  async getPublicLocaleDefaults(): Promise<{
    country_code: string | null;
    language: string;
    currency: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/public/locale-defaults`, {
      method: 'GET',
    });
    return handleResponse<{ country_code: string | null; language: string; currency: string }>(
      response,
    );
  },

  /**
   * Unauthenticated read of the same gate, for pre-auth screens that have no
   * setup token yet (the first signup screen, the email-verification result).
   * Returns only the global boolean — no tenant data.
   */
  async getPublicTenantApprovalRequired(): Promise<{ require_tenant_approval: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/public/tenant-approval-required`, {
      method: 'GET',
    });
    return handleResponse<{ require_tenant_approval: boolean }>(response);
  },

  /**
   * Get available modules for selection
   * If tenant_id provided, returns only modules enabled for that tenant
   */
  async getModules(token: string, tenantId?: string): Promise<{ modules: SetupModule[] }> {
    const url = tenantId
      ? `${API_BASE_URL}/api/v1/setup-wizard/modules?tenant_id=${tenantId}`
      : `${API_BASE_URL}/api/v1/setup-wizard/modules`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    return handleResponse<{ modules: SetupModule[] }>(response);
  },

  /**
   * Complete setup by joining an existing tenant
   */
  async joinTenant(token: string, data: JoinTenantRequest): Promise<SetupCompleteResponse> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/join-tenant`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    return handleResponse<SetupCompleteResponse>(response);
  },

  /**
   * Complete setup by creating a new tenant
   */
  async createTenant(token: string, data: CreateTenantRequest): Promise<SetupCompleteResponse> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/create-tenant`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    return handleResponse<SetupCompleteResponse>(response);
  },

  /**
   * Onboarding demo-data catalog: the variations + the industry-recommended key
   * to pre-select in the wizard's "Load sample data?" step (§4.4). Served to
   * pending_setup users (no tenant yet) — uses the explicit-token fetch pattern.
   */
  async getDemoCatalog(token: string, industry?: string | null): Promise<SetupDemoCatalog> {
    // Build the path + query by hand rather than `new URL()`: API_BASE_URL is ''
    // for same-origin web (dev proxy AND production), and `new URL('/api/...')`
    // with no absolute base throws "Invalid URL", silently emptying the catalog.
    const query = industry ? `?industry=${encodeURIComponent(industry)}` : '';
    const response = await fetch(`${API_BASE_URL}/api/v1/demo-data/onboarding-catalog${query}`, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    return handleRawResponse<SetupDemoCatalog>(response);
  },

  /**
   * Provision the caller's freshly-created tenant with a demo variation. Called
   * AFTER create-tenant succeeds and the new active token is in storage (the
   * tenant must exist and the caller be its admin). Returns nothing useful to the
   * wizard, so the response is ignored.
   */
  async provisionDemo(token: string, variation: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/v1/demo-data/provision`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ variation }),
    });
    await handleRawResponse<unknown>(response);
  },

  /**
   * Request to join a domain-matched tenant whose self-registration is OFF.
   * Creates a pending request a tenant admin must approve.
   */
  async createJoinRequest(
    token: string,
    data: { tenant_id: string; company_id?: string | null }
  ): Promise<MyJoinRequest & { tenant_name: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/join-request`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    return handleResponse<MyJoinRequest & { tenant_name: string }>(response);
  },

  /**
   * Get the current pending user's outstanding join request, or null.
   */
  async getMyJoinRequest(token: string): Promise<MyJoinRequest | null> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/join-request`, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    return handleResponse<MyJoinRequest | null>(response);
  },

  /**
   * Cancel the current pending user's join request (to create a tenant instead).
   */
  async cancelJoinRequest(token: string): Promise<{ cancelled: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/join-request`, {
      method: 'DELETE',
      headers: getAuthHeaders(token),
    });
    return handleResponse<{ cancelled: boolean }>(response);
  },

  /**
   * Decline a pending invitation
   */
  async declineInvitation(token: string): Promise<{ message_code: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/decline-invitation`, {
      method: 'POST',
      headers: getAuthHeaders(token),
    });
    return handleResponse<{ message_code: string }>(response);
  },

  /**
   * Get recommended modules for the company + the signer-up's role.
   *
   * The single source of module recommendations for the wizard: what the
   * company sells (`businessModels`, `business_classification.config.json`
   * model values) and its `industry` drive the ERP-shaped suggestions; job
   * function/level add on top.
   */
  async getRecommendedModules(
    token: string,
    jobFunction?: string | null,
    jobLevel?: string | null,
    businessModels?: string[],
    industry?: string | null
  ): Promise<{ recommended_modules: string[] }> {
    const params = new URLSearchParams();
    if (jobFunction) params.append('job_function', jobFunction);
    if (jobLevel) params.append('job_level', jobLevel);
    businessModels?.forEach((model) => params.append('business_model', model));
    if (industry) params.append('industry', industry);

    const url = params.toString()
      ? `${API_BASE_URL}/api/v1/setup-wizard/recommended-modules?${params}`
      : `${API_BASE_URL}/api/v1/setup-wizard/recommended-modules`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    return handleResponse<{ recommended_modules: string[] }>(response);
  },

  /**
   * Get subdomain suggestion based on user's email domain
   */
  async getSubdomainSuggestion(token: string): Promise<SubdomainSuggestion> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/subdomain-suggestion`, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    return handleResponse<SubdomainSuggestion>(response);
  },

  /**
   * Check whether a user-typed subdomain can be claimed (live, debounced).
   * Distinct from getSubdomainSuggestion, which only evaluates the
   * email-derived name. Read-only — never reserves the subdomain.
   */
  async checkSubdomainAvailability(
    token: string,
    subdomain: string
  ): Promise<SubdomainAvailability> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/setup-wizard/subdomain-availability?subdomain=${encodeURIComponent(subdomain)}`,
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
    return handleResponse<SubdomainAvailability>(response);
  },

  /**
   * Get AI prefill for the v2 wizard (company + person + recommended modules).
   * Backend short-polls its cache; status may still be 'pending' on return.
   */
  async getPrefill(token: string): Promise<SetupWizardPrefill> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/prefill`, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    return handleResponse<SetupWizardPrefill>(response);
  },

  /**
   * Confirm a LinkedIn candidate (or pasted URL) and extract the full person profile.
   */
  async confirmPerson(token: string, data: ConfirmPersonRequest): Promise<PersonProfile> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/confirm-person`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    return handleResponse<PersonProfile>(response);
  },

  /**
   * Resolve a company name or website to a fresh company prefill (generic-domain fallback).
   */
  async fallbackResolve(token: string, query: string): Promise<FallbackResolveResponse> {
    const response = await fetch(`${API_BASE_URL}/api/v1/setup-wizard/fallback-resolve`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ query }),
    });
    return handleResponse<FallbackResolveResponse>(response);
  },
};

export { SetupWizardApiError };
